import { useEffect, useState } from "react";

import {
  Select,
  Button,
  AutocompleteMultipleSelect,
  TableMetadata,
  TableStateWithFilters,
  useTable,
  ColumnSortEnum,
  Spinner,
} from "@king-ict/eupisi-ui/components";
import { PagedResult } from "@king-ict/eupisi-ui/models";
import {
  codeBookToSelectData,
  hrDate,
  nameof,
} from "@king-ict/eupisi-ui/utils";
import { Row, Col, Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import {
  getDepartmentFilteredList,
  editDepartment,
  deleteDepartment,
  createDepartment,
} from "../../api/Department/DepartmentApi";
import { searchInstitutionNew } from "../../api/Institution/InstitutionApi";
import { useAppDispatch } from "../../app/store";
import {
  ContentArea,
  FormCard,
  ListTable,
  SearchCard,
  ToolbarTop,
  InstitutionSelect,
} from "../../components";
import DepartmentModal from "../../features/Departments/DepartmentModal";
import { useDidUpdateEffect, useInput } from "../../hooks";
import { useAuth } from "../../hooks/auth/useAuth";
import {
  SchoolYear,
  ValidationFailure,
  InstitutionCodeBook,
  Role,
} from "../../models";
import {
  Department,
  DepartmentListSearch,
  DepartmentListSort,
  DepartmentEditRequest,
  DepartmentCreateRequest,
} from "../../models/Department";
import { ImportantDateEventEnum } from "../../models/ImportantDate/ImportantDateEventEnum";
import { InstitutionCodeBookSearch } from "../../models/Institution";
import { ClaimType } from "../../models/User";
import {
  useGetInstitutionListForUserQuery,
  useGetSchoolYearCurrentQuery,
  useGetSchoolYearListQuery,
} from "../../queries/codeBookQuery";
import { TableUtils } from "../../utils";

/**
 * Only Global and County level users.
 * @returns
 */
export function DepartmentList() {
  const { t } = useTranslation();
  const history = useHistory();
  const { handleChange } = useInput();
  const dispatch = useAppDispatch();
  const auth = useAuth();
  const { data: currentSchoolYear } = useGetSchoolYearCurrentQuery();
  const { data: schoolYears } = useGetSchoolYearListQuery();
  const { data: institutions } = useGetInstitutionListForUserQuery();

  //DELETE / EDIT
  const [departmentToDelete, setDepartmentToDelete] =
    useState<Department | null>(null);
  const [selectedDepartment, setSelectedDepartment] =
    useState<Department | null>(null);

  //MODAL
  const [showModal, setShowModal] = useState<boolean>(false);

  //STORAGE KEY
  const storageKey = "DepartmentListSearchTableState";
  //INIT TABLE STATE WITH FILTERS - could be temporary stored in local session storage
  const createInitialTableState = () => {
    return TableUtils.CreateInitialTableState<DepartmentListSearch>(
      storageKey,
      TableUtils.tableDefaultState(
        DepartmentListSort.Label,
        ColumnSortEnum.ASC,
        50
      )
    );
  };
  const [initialTableState] = useState<
    TableStateWithFilters<DepartmentListSearch>
  >(() => createInitialTableState());
  //INIT SEARCH FILTER

  const defaultInstitutionId: number =
    // location only represents an Institution if ClaimType = Institution
    auth.currentRole?.claimType === ClaimType.Institution &&
    (auth.currentRole?.locations?.length ?? 0) > 0 &&
    auth.currentRole?.locations &&
    auth.currentRole?.locations[0]
      ? auth!.currentRole!.locations![0]
      : ((institutions && institutions.length > 0
          ? institutions[0].id
          : 0) as number);
  const createInitialDepartmentListSearch = (): DepartmentListSearch => {
    return {
      schoolYearId:
        initialTableState.schoolYearId ??
        (schoolYears && schoolYears.length > 0 ? schoolYears[0].id : 0),
      institutionId: initialTableState.institutionId ?? defaultInstitutionId,
    };
  };

  //TABLE DATA
  const defaultTableData = TableUtils.tableDefaultData<Department>();
  const [tableData, setTableData] =
    useState<PagedResult<Department>>(defaultTableData);
  //SET SEARCH FILTERS
  const [departmentListSearch, setDepartmentListSearch] =
    useState<DepartmentListSearch>(() => createInitialDepartmentListSearch());
  const [tableSearch, setTableSearch] = useState<DepartmentListSearch>(() =>
    createInitialDepartmentListSearch()
  );
  const [filterChanged, setFilterChanged] = useState<boolean>(false);
  const [clearFilter, setClearFilter] = useState<boolean>(true);
  //TABLE METADATA
  const tableMetadata: TableMetadata<Department> = {
    rowKey: "id",
    columns: [
      {
        columnKey: "label",
        header: t("common.departmentList.nameOfDepartment"),
        accessor: (item: Department) => {
          return (
            <div>
              <div className={"text-nowrap"}>
                {item.label?.trim().toUpperCase()}
              </div>
            </div>
          );
        },
        sortBy: DepartmentListSort.Label,
      },
      {
        columnKey: "fullLabel",
        header: t("common.departmentList.finalNameOfDepartment"),
        accessor: (item: Department) => {
          return (
            <div>
              <div className={"text-nowrap"}>
                {`1. ${item.label?.trim().toUpperCase()}`.trim()}
              </div>
            </div>
          );
        },
        sortBy: DepartmentListSort.Label,
      },
      {
        columnKey: "dateCreated",
        header: t("common.departmentList.date"),
        accessor: (item: Department) => {
          return (
            <div>
              <div className={"text-nowrap"}>{hrDate(item.dateCreated)}</div>
            </div>
          );
        },
        sortBy: DepartmentListSort.CreationDate,
      },
      {
        columnKey: "lastUpdateUser",
        header: t("common.departmentList.userName"),
        accessor: (item: Department) => {
          return (
            <div>
              <div className={"text-nowrap"}>{item.lastUpdateUser}</div>
            </div>
          );
        },
        sortBy: DepartmentListSort.LastUpdateUser,
      },
      {
        columnKey: "deleteEdit",
        header: t("common.departmentList.deleteEdit"),
        accessor: (item: Department) => {
          return (
            <div className="text-nowrap">
              {/*<button
                                    className="btn btn-danger btn-sm"
                                    onClick={() => handleDelete(item)}>
                                        {t("common.departmentList.delete")}
                                </button>
                                <button
                                    className="btn btn-primary btn-sm"
                                    style={{marginLeft: '15px'}}
                                    onClick={() => handleEdit(item)}>
                                        {t("common.departmentList.edit")}
                                </button>
                        */}
              <Button
                color={"danger"}
                outline={true}
                size={"sm"}
                onClick={() => handleDelete(item)}
              >
                {t("common.departmentList.delete")}
              </Button>
              &nbsp;
              <Button size={"sm"} onClick={() => handleEdit(item)}>
                {t("common.departmentList.edit")}
              </Button>
            </div>
          );
        },
      },
    ],
    rowToolTip: () => undefined,
  };

  //UPDATE TABLE DATA WHEN DEPARTMENTS CHANGE

  //SET TABLE
  /** The table metadata. The search is performed when the tableState changes (useEffect)
   * which is triggerd by a call to setFilters. Why are we doing this?
   */

  const [tableProps, tableState, setFilters] = useTable<DepartmentListSearch>({
    metadata: tableMetadata,
    data: tableData!.results,
    pagination: true,
    paginationPage: initialTableState.page,
    paginationPageSize: initialTableState.pageSize as any,
    totalItems: tableData!.rowCount,
    defaultSort: {
      order: initialTableState.order,
      sortBy: initialTableState.sortBy,
    },
  });

  //EDIT DATA

  //EDIT DATA
  const [isSchoolYearEditable, setIsSchoolYearEditable] =
    useState<boolean>(false);
  const [errors, setErrors] = useState<ValidationFailure[]>();
  const [initialSearch, setInitialSearch] = useState<boolean>(false);

  //    useDidUpdateEffect(() => {
  //        //used when user refresh/reload page
  //        const newDepartmentListSearch: DepartmentListSearch = {
  //            ...departmentListSearch,
  //        }
  //        if (currentSchoolYear && departmentListSearch.schoolYearId !== currentSchoolYear?.id && schoolYears) {
  //            newDepartmentListSearch.schoolYearId = currentSchoolYear?.id;
  //        }
  //        setDepartmentListSearch(newDepartmentListSearch);
  //        setTableSearch(newDepartmentListSearch);
  //        setClearFilter(true);
  //        search();
  //    // eslint-disable-next-line react-hooks/exhaustive-deps
  //    }, [currentSchoolYear, schoolYears /*, institutions*/]);

  useEffect(() => {
    if (clearFilter) {
      setClearFilter(false);
      setFilterChanged(false);
    } else setFilterChanged(true);
    TableUtils.RemoveSessionStorageItem(storageKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departmentListSearch]);

  useEffect(() => {
    if (
      schoolYears &&
      schoolYears.length > 0 &&
      !departmentListSearch.schoolYearId
    ) {
      var schoolYear = schoolYears.find((v) => v.isCurrent);
      if (schoolYear) {
        const newSearch: DepartmentListSearch = {
          ...departmentListSearch,
          //institutionIds: institutionList && institutionList.length > 0 ? institutionList.map(val => val.id) : [],
          schoolYearId: schoolYear.id,
        };
        setDepartmentListSearch(newSearch);
      }
    }
  }, [schoolYears]);

  useEffect(() => {
    if (
      institutions &&
      institutions.length > 0 &&
      !departmentListSearch.institutionId
    ) {
      var institution = institutions[0];
      const newSearch: DepartmentListSearch = {
        ...departmentListSearch,
        institutionId: institution.id,
      };
      setDepartmentListSearch(newSearch);
    }
  }, [institutions]);

  useEffect(() => {
    (async () => {
      if (
        departmentListSearch.schoolYearId &&
        departmentListSearch.institutionId &&
        !initialSearch
      ) {
        handleSearchClicked(null);
        setInitialSearch(true);
      }
    })();
  }, [departmentListSearch.schoolYearId, departmentListSearch.institutionId]);

  useEffect(() => {
    const request = TableUtils.GetSearchRequest<DepartmentListSearch>(
      tableSearch,
      tableState
    );
    if (!request?.schoolYearId || !request.institutionId) return;
    (async () => {
      console.log("request: ", request);
      const itemList = await getDepartmentFilteredList(request);
      console.log("Api response: ", itemList);
      if (itemList !== null) {
        const pagedResult: PagedResult<Department> = {
          ...itemList,
          results: itemList.results,
        };
        setTableData(pagedResult);
      } else {
        setTableData(defaultTableData);
      }
      setSchoolYearEditable(request!.schoolYearId!);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableState]);

  const handleEdit = (department: Department) => {
    setSelectedDepartment(department);
    setShowModal(true);
  };

  const handleDelete = (department: Department) => {
    setDepartmentToDelete(department);
  };

  const confirmDelete = async () => {
    if (departmentToDelete) {
      const department = departmentToDelete;
      setDepartmentToDelete(null);
      const result = await deleteDepartment(departmentToDelete.id);
      if (result) {
        setErrors(result.validationFailures);
        if (result.isValid) {
          const updatedResults = tableData.results.filter(
            (d) => d.id !== departmentToDelete.id
          );
          const updatedTableData: PagedResult<Department> = {
            ...tableData,
            results: updatedResults,
            rowCount: tableData.rowCount - 1,
          };
          setTableData(updatedTableData);
        }
      }
    }
  };

  const cancelDelete = () => {
    setDepartmentToDelete(null);
  };

  //FILTER HANDLERS
  const setSchoolYearEditable = (yearId: number) => {
    var year = schoolYears?.find((val) => val.id === yearId);
    setIsSchoolYearEditable(!!year && year.isCurrent);
  };

  const handleInstitutionChange = (id: number) => {
    const newDepartmentListSearch: DepartmentListSearch = {
      ...departmentListSearch,
      institutionId: !isNaN(id) ? id : undefined,
    };
    setDepartmentListSearch(newDepartmentListSearch);
    setFilterChanged(true);
  };
  const handleOpenModal = () => {
    setShowModal(true);
    setSelectedDepartment(null);
  };

  const fetchDepartmentList = async () => {
    const request = TableUtils.GetSearchRequest<DepartmentListSearch>(
      tableSearch,
      tableState
    );
    const itemList = await getDepartmentFilteredList(request);

    if (itemList !== null) {
      const pagedResult: PagedResult<Department> = {
        ...itemList,
        results: itemList.results,
      };
      setTableData(pagedResult);
    } else {
      setTableData(defaultTableData);
    }
  };

  const handleCloseModal = () => setShowModal(false);
  const handleSave = async (
    label: string,
    fullLabel: string
  ): Promise<void> => {
    const upperCaseLable = label.toUpperCase();
    const schoolYearId = departmentListSearch.schoolYearId;
    const institutionId = departmentListSearch.institutionId;

    if (selectedDepartment) {
      const departmentEditRequest: DepartmentEditRequest = {
        id: selectedDepartment.id,
        label: upperCaseLable,
      };

      const response = await editDepartment(departmentEditRequest);
      if (response) {
        setErrors(response.validationFailures);
        if (response.isValid) {
          const updateDepartments = tableData.results.map((department) =>
            department.id === response.result?.id
              ? {
                  ...department,
                  label: response.result?.label,
                  fullLabel: fullLabel,
                }
              : department
          );
          setTableData({
            ...tableData,
            results: updateDepartments,
          });
          console.log("Update: ", response.result);
        }
      }
    } else {
      const newDepartmentRequest: DepartmentCreateRequest = {
        schoolYearId: schoolYearId!,
        institutionId: institutionId,
        label: upperCaseLable,
      };

      const response = await createDepartment(newDepartmentRequest);
      if (response) {
        setErrors(response.validationFailures);
        if (response.isValid) {
          await fetchDepartmentList();
          handleCloseModal();
        }
      }
    }
  };

  const handleClearSearchClicked = () => {
    setClearFilter(true);
    const newSearch: DepartmentListSearch = {
      schoolYearId: schoolYears?.find((v) => v.isCurrent)?.id ?? 0,
      institutionId: defaultInstitutionId,
    };
    setDepartmentListSearch(newSearch);
    setTableSearch(newSearch);
    setSchoolYearEditable(newSearch.schoolYearId!);
    setFilterChanged(false);
    search();
  };

  const handleSearchClicked = (e: any) => {
    setTableSearch(departmentListSearch);
    setFilterChanged(false);
    search();
  };
  const search = () => {
    setFilters({ ...tableSearch });
  };

  if (!schoolYears || !currentSchoolYear) return <Spinner show={true} />;
  return (
    <ContentArea>
      <ToolbarTop pageTitle={t("navigation.departmentList")} errors={errors} />
      <SearchCard
        onClickSearch={handleSearchClicked}
        onClickClearSearch={handleClearSearchClicked}
        isFilterChanged={filterChanged}
        hideDivider={true}
      >
        <Row>
          <Col md={6}>
            <Select
              label={t("common.departmentList.schoolYearId")}
              name={nameof<typeof departmentListSearch>("schoolYearId")}
              value={departmentListSearch.schoolYearId}
              required={true}
              onChange={(e) => {
                setFilterChanged(true);
                handleChange(setDepartmentListSearch);
              }}
              selectData={codeBookToSelectData(schoolYears)}
            />
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <InstitutionSelect
              label={t("common.departmentList.institutionList")}
              name={nameof<typeof departmentListSearch>("institutionId")}
              value={departmentListSearch.institutionId}
              countyIds={
                auth.currentRole?.claimType === ClaimType.County
                  ? auth.currentRole.locations
                  : undefined
              }
              ids={
                auth.currentRole?.claimType === ClaimType.Institution
                  ? auth.currentRole.locations
                  : undefined
              }
              onlyByEnrollmentArea={false}
              excludeHandicapped={false}
              excludePrivate={false}
              excludeSatellite={false}
              onChange={(e) =>
                handleInstitutionChange(Number.parseInt(e.target.value))
              }
              showUser={true}
              allowEmpty={false}
            />
          </Col>
        </Row>
      </SearchCard>
      <FormCard>
        <Row className="m-1 pb-3 pt-3 bg-light border">
          <Col md={2} className="pt-2">
            <Button
              onClick={handleOpenModal}
              className="mt-1"
              size="sm"
              disabled={
                !isSchoolYearEditable ||
                !tableSearch.institutionId ||
                filterChanged
              }
            >
              {t("common.departmentList.addDepartment")}
            </Button>
          </Col>

          <DepartmentModal
            show={showModal}
            handleClose={handleCloseModal}
            handleSave={handleSave}
            department={selectedDepartment}
            existingDepartment={tableData.results.map((department) =>
              department.label.toUpperCase()
            )}
          />
        </Row>
      </FormCard>
      <ListTable
        tableProps={tableProps}
        pageSizeOptions={[50, 100, 200, 300, 500, 1000, 1500, 2000, 6000]}
      />
      {departmentToDelete && (
        <Modal show={true} onHide={cancelDelete} centered>
          <Modal.Body>{t("common.departmentList.deleteConfirm")}</Modal.Body>
          <Modal.Footer>
            <Button className="btn btn-primary" onClick={cancelDelete}>
              {t("common.departmentList.quit")}
            </Button>
            <Button className="btn btn-danger" onClick={confirmDelete}>
              {t("common.departmentList.delete")}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </ContentArea>
  );
}
